import moment from "moment";
import dateFormats from "app/data/common/dateFormats";
import { downloadCsvFile } from "app/data/common/exportToCsv";
import { LoadboardListManifestTrip } from "app/data/load-board/models";

const formatUiDate = (value?: string | null): string => {
  if (!value) return "";
  return (value as any).toUiDateFormat?.() ?? value;
};

const formatUiTime = (value?: string | null): string => {
  if (!value) return "";
  return (value as any).toUiTimeFormat?.() ?? value;
};

const formatTerminal = (
  airportCode?: string | null,
  name?: string | null,
  number?: string | number | null
): string => {
  const left = [airportCode, name].filter(Boolean).join(" - ");
  if (left && number != null && number !== "") return `${left} (${number})`;
  if (left) return left;
  if (number != null && number !== "") return String(number);
  return "";
};

const formatLoadPercent = (loadedWeight?: number | null): string => {
  const weight = Number(loadedWeight) || 0;
  const MAX_CAPACITY = 42000;
  const pct = Math.min(Math.round((weight / MAX_CAPACITY) * 100), 100);
  return `${pct}%`;
};

const getTripsForManifest = (
  row: LoadboardListManifestTrip,
  tripsByManifest?: Map<number | string, LoadboardListManifestTrip[]>
): LoadboardListManifestTrip[] => {
  const trips =
    tripsByManifest?.get(Number(row.manifestNumber)) ??
    tripsByManifest?.get(row.manifestNumber as any) ??
    [];
  return [...trips].sort((a, b) => Number(a.tripNumber) - Number(b.tripNumber));
};

/**
 * One row per parent manifest — Phase 1 schedule columns (origin + final destination).
 */
export const exportManifestSummaryCsv = (
  rows: LoadboardListManifestTrip[],
  tripsByManifest?: Map<number | string, LoadboardListManifestTrip[]>
): void => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const headers = [
    "Manifest #",
    "Door",
    "Priority",
    "Trips",
    "Origin Airport Code",
    "Origin Terminal Name",
    "Origin Terminal Number",
    "Stop",
    "Destination Airport Code",
    "Destination Terminal Name",
    "Destination Terminal Number",
    "Lane Status",
    "Load %",
    "Loaded Weight",
    "Carrier Name",
    "Carrier Number",
    "Plan Close Date",
    "Plan Close Time",
    "Dispatch Date",
    "Dispatch Time",
    "Est Departure from Origin Date",
    "Est Departure from Origin Time",
    "Actual Departure from Origin Date",
    "Actual Departure from Origin Time",
    "Est Arrival at Destination Date",
    "Est Arrival at Destination Time",
    "Actual Arrival at Destination Date",
    "Actual Arrival at Destination Time",
    "Driver Name",
    "Driver ID",
    "Trailer",
    "Tractor",
    "Timezone",
  ];

  const csvRows = rows.map((row) => {
    const sorted = getTripsForManifest(row, tripsByManifest);
    const first = sorted[0];
    const middle = sorted.length >= 2 ? sorted[1] : null;
    const last = sorted[sorted.length - 1];
    const highestTrip = sorted.length
      ? Math.max(...sorted.map((t) => Number(t.tripNumber)))
      : row.tripNumber ?? "";

    const loadedWeight = Number((row as any).loadedWeight) || 0;
    const originSource = first ?? row;
    const destinationSource = last ?? row;

    return [
      row.manifestNumber ?? "",
      row.doorNumber ? `D-${row.doorNumber}` : "",
      (row as any).doorPriority ?? "",
      highestTrip,
      first?.originTerminalAirportCode ?? row.originTerminalAirportCode ?? "",
      first?.originTerminalName ?? row.originTerminalName ?? "",
      first?.originTerminalNumber ?? row.originTerminalNumber ?? "",
      middle
        ? formatTerminal(
            middle.originTerminalAirportCode,
            middle.originTerminalName,
            middle.originTerminalNumber
          )
        : "",
      last?.destinationTerminalAirportCode ?? row.destinationTerminalAirportCode ?? "",
      last?.destinationTerminalName ?? row.destinationTerminalName ?? "",
      last?.destinationTerminalNumber ?? row.destinationTerminalNumber ?? "",
      row.doorManifestStatus ?? "",
      formatLoadPercent(loadedWeight),
      loadedWeight || "",
      row.carrierName ?? "",
      row.carrierNumber ?? "",
      formatUiDate(row.plannedCloseDate),
      formatUiTime(row.plannedCloseTime),
      formatUiDate(row.dispatchDate),
      formatUiTime(row.dispatchTime),
      formatUiDate(originSource.estimatedDepartureDate),
      formatUiTime(originSource.estimatedDepartureTime),
      formatUiDate(originSource.originActualDepartureDate),
      formatUiTime(originSource.originActualDepartureTime),
      formatUiDate(destinationSource.finalDestinationDueDate),
      formatUiTime(destinationSource.finalDestinationDueTime),
      formatUiDate(destinationSource.finalDestinationActualArrivalDate),
      formatUiTime(destinationSource.finalDestinationActualArrivalTime),
      row.driverName ?? row.driver ?? "",
      row.driverId ?? "",
      row.trailerNumber ? `TRL - ${row.trailerNumber}` : "",
      row.tractorNumber ? `TRC - ${row.tractorNumber}` : "",
      timezone,
    ];
  });

  downloadCsvFile(
    `loadboard-manifests-${moment().format(dateFormats.uiDate)}.csv`,
    headers,
    csvRows
  );
};

/**
 * One row per trip leg — Phase 2 schedule columns (expand sub-row data).
 */
export const exportLegScheduleCsv = (
  rows: LoadboardListManifestTrip[],
  tripsByManifest?: Map<number | string, LoadboardListManifestTrip[]>
): void => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const headers = [
    "Manifest #",
    "Trip #",
    "Origin Airport Code",
    "Origin Terminal Name",
    "Origin Terminal Number",
    "Destination Airport Code",
    "Destination Terminal Name",
    "Destination Terminal Number",
    "Est Departure Date",
    "Est Departure Time",
    "Actual Departure Date",
    "Actual Departure Time",
    "Est Arrival Date",
    "Est Arrival Time",
    "Actual Arrival Date",
    "Actual Arrival Time",
    "Timezone",
  ];

  const csvRows: (string | number)[][] = [];

  for (const row of rows) {
    const sorted = getTripsForManifest(row, tripsByManifest);
    const legs = sorted.length > 0 ? sorted : [row];

    for (const leg of legs) {
      csvRows.push([
        leg.manifestNumber ?? row.manifestNumber ?? "",
        leg.tripNumber ?? "",
        leg.originTerminalAirportCode ?? "",
        leg.originTerminalName ?? "",
        leg.originTerminalNumber ?? "",
        leg.destinationTerminalAirportCode ?? "",
        leg.destinationTerminalName ?? "",
        leg.destinationTerminalNumber ?? "",
        formatUiDate(leg.estimatedDepartureDate),
        formatUiTime(leg.estimatedDepartureTime),
        formatUiDate(leg.legActualDepartureDate),
        formatUiTime(leg.legActualDepartureTime),
        formatUiDate(leg.legEstArrivalDate),
        formatUiTime(leg.legEstArrivalTime),
        formatUiDate(leg.legActualArrivalDate),
        formatUiTime(leg.legActualArrivalTime),
        timezone,
      ]);
    }
  }

  downloadCsvFile(
    `loadboard-legs-${moment().format(dateFormats.uiDate)}.csv`,
    headers,
    csvRows
  );
};

/**
 * Download manifest summary CSV + leg schedule CSV (Phase 1 + Phase 2).
 */
const exportLoadBoardToCsv = (
  rows: LoadboardListManifestTrip[],
  tripsByManifest?: Map<number | string, LoadboardListManifestTrip[]>
): void => {
  exportManifestSummaryCsv(rows, tripsByManifest);
  exportLegScheduleCsv(rows, tripsByManifest);
};

export default exportLoadBoardToCsv;
